import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { commonImages } from '@images/common-images';
import { createReport } from '@modules/reports/reports-data';
import { LeadManagementService } from '@services/lm-service/lead-management.service';
import { MessageService } from '@services/util/message.service';

@Component({
  selector: 'app-remmi-date-range',
  templateUrl: './remmi-date-range.component.html',
  styleUrls: ['./remmi-date-range.component.scss'],
})
export class RemmiDateRangeComponent implements OnDestroy {
  @Input() isSevenDays: boolean = true;
  /**
   * Opt-in: pin the panel to the viewport (`position: fixed`) instead of hanging it off the box.
   * For hosts inside a scrolling or clipped container (a dialog body) where the absolute panel was
   * cut off. It opens below the box, or above when there is not room below.
   */
  @Input() fixedPanel: boolean = false;
  @Output() applyDateFilter = new EventEmitter<any>();

  @ViewChild('box') private boxRef?: ElementRef<HTMLElement>;
  @ViewChild('panel') private panelRef?: ElementRef<HTMLElement>;

  dateRange: any[] = [];
  common_images = commonImages;
  isDateRangeDisplay: boolean = false;
  dateName: string = '';
  daterRange: any = null;
  /** Viewport position for the fixed panel; hidden until measured so it never flashes in the wrong spot. */
  panelStyle: Record<string, string> = { visibility: 'hidden' };

  private readonly onAnyScroll = () => this.positionPanel();

  constructor(
    private messageService: MessageService,
    public lmService: LeadManagementService
  ) {}

  ngOnInit(): void {
    this.dateRange = JSON.parse(JSON.stringify(createReport.loadDate_Range()));
    console.log('this.dateRange >>', this.dateRange);
    let currenMonth = this.dateRange?.find(item => (this.lmService.isShowAllRecords ? item?.name == 'All' : item?.name == 'Last 7 Days'));
    this.isSevenDays && (this.dateName = currenMonth?.name);
    this.isSevenDays && this.applyDateFilter.emit(currenMonth);
    this.messageService.getMessage().subscribe({
      next: response => {
        this.lmService.isDateReset && (this.dateName = null);
        if (response?.isClearItems && this.isSevenDays) {
          this.dateName = this.lmService.isShowAllRecords ? 'All' : 'Last 7 Days';
        }

        setTimeout(() => {
          this.lmService.isDateReset = false;
        }, 1500);
      },
    });
  }

  ngOnDestroy(): void {
    this.stopTracking();
    // A panel moved to <body> is not inside this host any more, so tearing the host down would leave it behind.
    const panel = this.panelRef?.nativeElement;
    if (panel?.parentNode === document.body) {
      document.body.removeChild(panel);
    }
  }

  /**
   * Outside-click for `fixedPanel` mode. The panel lives on <body> there, so `clickOutside` (which
   * only knows the box) would call a click inside the panel — the Custom date input — "outside".
   */
  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: any): void {
    if (!this.fixedPanel || !this.isDateRangeDisplay || !(target instanceof Element) || !document.contains(target)) {
      return;
    }
    const isInside =
      this.boxRef?.nativeElement.contains(target) ||
      this.panelRef?.nativeElement.contains(target) ||
      !!target.closest('.p-datepicker, .p-ripple, .p-link, .ng-dropdown-panel');
    if (!isInside) {
      this.isDateRangeDisplay = false;
      this.stopTracking();
    }
  }

  toggleDisplay(): void {
    this.isDateRangeDisplay = !this.isDateRangeDisplay;
    if (!this.fixedPanel) {
      return;
    }
    if (this.isDateRangeDisplay) {
      this.panelStyle = { visibility: 'hidden' };
      // Measured after render; scroll is caught in the capture phase so a scrolling dialog body
      // (not just the window) keeps the panel glued to its box.
      setTimeout(() => this.positionPanel());
      document.addEventListener('scroll', this.onAnyScroll, true);
    } else {
      this.stopTracking();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.positionPanel();
  }

  private positionPanel(): void {
    if (!this.fixedPanel || !this.isDateRangeDisplay || !this.boxRef) {
      return;
    }
    const rect = this.boxRef.nativeElement.getBoundingClientRect();
    const panel = this.panelRef?.nativeElement;
    if (!panel) {
      // Not rendered yet — measure on the next tick rather than leave it hidden.
      setTimeout(() => this.positionPanel());
      return;
    }
    // Portal to <body> (as PrimeNG's appendTo="body" does): inside a dialog, a transformed or
    // overflow-hidden ancestor still traps a `position: fixed` child and the panel never shows.
    if (panel && panel.parentNode !== document.body) {
      document.body.appendChild(panel);
    }
    const width = panel?.offsetWidth || 300;
    const height = panel?.offsetHeight || 0;
    const gap = 4;
    const edge = 8;
    const left = Math.min(Math.max(rect.right - width, edge), window.innerWidth - width - edge);
    let top = rect.bottom + gap;
    if (top + height > window.innerHeight - edge) {
      const above = rect.top - height - gap;
      top = above >= edge ? above : Math.max(edge, window.innerHeight - height - edge);
    }
    this.panelStyle = { top: `${top}px`, left: `${Math.max(edge, left)}px` };
  }

  private stopTracking(): void {
    document.removeEventListener('scroll', this.onAnyScroll, true);
  }

  onOutDateRange(e: Event) {
    // `fixedPanel` handles its own outside clicks in onDocumentClick().
    if (this.fixedPanel) {
      return;
    }
    if (e['bol'] == false && this.isDateRangeDisplay) {
      this.isDateRangeDisplay = false;
      this.stopTracking();
    }
  }

  customDateSelect() {
    const [start_date, end_date] = this.daterRange;
    let payload = {
      id: 1501,
      name: 'Custom Date',
      format: 'dd MMM YYYY',
      from: start_date,
      to: end_date ? end_date : start_date,
      isChange: true,
    };
    this.dateName = this.convertDate(start_date) + (end_date ? ' - ' : '') + this.convertDate(end_date);
    this.applyDateFilter.emit(payload);
  }

  isSelected(item: any) {
    console.log('item :>> ', item);
    // Single-day presets (e.g. Today) show one date; ranges show "from - to".
    this.dateName = this.isSingleDay(item)
      ? this.convertDate(item.from)
      : this.convertDate(item.from) + (item.to ? ' - ' : '') + this.convertDate(item.to);
    this.daterRange = null;
    this.isDateRangeDisplay = false;
    this.stopTracking();
    if (item?.name === 'All') {
      this.dateName = item.name;
    }
    this.applyDateFilter.emit({ ...item, isChange: true });
  }

  /** True when a preset covers a single calendar day (from/to same date). */
  isSingleDay(item: any): boolean {
    if (!item?.from) return false;
    if (!item?.to) return true;
    return this.convertDate(item.from) === this.convertDate(item.to);
  }

  convertDate(date: Date): string {
    if (!date) {
      return '';
    }
    const d = new Date(date);

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  }

  selectToday(event: any) {
    event.stopPropagation(); // prevent calendar close if needed

    const today = new Date();
    this.daterRange = [this.daterRange[0] ? this.daterRange[0] : today, today];
  }

  clearCustomRange(event: Event): void {
    event.stopPropagation();
    this.daterRange = null;
  }

  /** Enables the OK button once a start date is picked — a range's end date is optional (see customDateSelect). */
  get canApply(): boolean {
    return !!this.daterRange?.[0];
  }

  /** Apply the picked range from the OK button, then close both the calendar overlay and this dropdown. */
  applyCustomRange(event: Event, cal?: { hideOverlay?: () => void }): void {
    event.stopPropagation();
    this.customDateSelect();
    this.isDateRangeDisplay = false;
    this.stopTracking();
    cal?.hideOverlay?.();
  }
}
